import { httpGet, httpPost, httpPostRaw, httpPut } from "@eres/rest";
import cfg from "./config";
import {
	ApplicationProfiles,
	CorrectionTak,
	EIDAServerProfile,
	Profile, ProfileResponse,
	ProfileUpdateRequest,
	ProfileUpdateResponse,
	ReqStatus,
	SSOLoginData,
	SSOLoginVerifyData,
	SSOResponse
} from "./entities";
import { later } from "@my-dld/utils/Utils";
import { LocaleUtil, StringUtil } from "@eres/core";
import { RegType } from "@my-dld/contexts/RegisterContext";
import { SSOApi } from "./index";
import Cookies from "universal-cookie";

export const MockConfig = {
	mock: window.mockSSOApi,
	signedIn: false,
	loadingTime: 1000,
};

interface ILoginApi {
	eid( eid: string, captchaResponse: string ): Promise<SSOResponse<SSOLoginData>>;

	resendOTP(): Promise<any>;

	verifyOTP( otp: string, method: "eid" | string ): Promise<SSOResponse<SSOLoginVerifyData>>;

	verifyPassCode( passCode: string, method: "eid" | string ): Promise<SSOResponse<SSOLoginVerifyData>>;

	generatePassCode( method: "eid" | string ): Promise<SSOResponse<SSOLoginData>>;

	user( user: string, password: string, captchaResponse: string ): Promise<SSOResponse<SSOLoginVerifyData>>;
}

interface IProfileApi {
	me(): Promise<SSOResponse<Profile>>;

	logout(): Promise<any>;

	changePassword( old: string, newPwd: string ): Promise<any>;

	copy( appId: string, userName: string, password: string ): Promise<any>;

	createUpdateToken(): Promise<SSOResponse<ReqStatus>>;

	verifyUpdateToken( request: ProfileUpdateRequest ): Promise<SSOResponse<ReqStatus>>;

	initUpdate( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>>;

	resendOTPForUpdate( request: { email: boolean, mobile: boolean } ): Promise<SSOResponse<ProfileUpdateResponse>>;

	verifyOTPForUpdate( request: { email?: string, mobile?: string } ): Promise<SSOResponse<ProfileUpdateResponse>>;

	handleProfileException( error: SSOError ): Promise<SSOCorrectiveAction>;

	getProfilePicUri(): string;

	updateAvatar( photoBase64: string ): Promise<any>;

	initAccountVerify( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>>;

	verifyAccountOtp( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>>;

	selectProfile(userAccountId: string): Promise<any> ;

	fetchProfiles(appName: string): Promise<ProfileResponse<ApplicationProfiles>> ;

	fetchNDAs(applicationId: string): Promise<ProfileResponse<any>> ;

	submitNDA(data: any): Promise<ProfileResponse<any>> ;
}

interface IForgotApi {
	init: ( userName: string, captchaResponse: string, mobile?: string, email?: string ) => Promise<any>;

	resendOTP(): Promise<any>;

	verify( otp: string ): Promise<any>;

	update( newPassword: string ): Promise<any>;
}

interface IRegisterApi {
	eid( eid: string, captchaResponse: string ): Promise<SSOResponse<EIDAServerProfile>>;

	confirm( profile: EIDAServerProfile ): Promise<SSOResponse<EIDAServerProfile>>;

	verify( mobileOTP: string, emailOTP: string ): Promise<SSOResponse<EIDAServerProfile>>;

	resendOTP( config: { email: boolean; mobile: boolean } ): Promise<any>;

	create( regType: RegType, userName: string, password: string ): Promise<SSOResponse<EIDAServerProfile>>;

	loadUaePassProfile(): Promise<SSOResponse<EIDAServerProfile>>;

	validateApplication( appId: string ): Promise<SSOResponse<any>>;
}

export const login: ILoginApi = {
	eid: ( eid, captchaResponse ) => {
		if ( eid ) eid.replace( /^[0-9]*/g, "" );
		const req = {
			emiratesId: eid,
			reCaptchaKey: captchaResponse
		};
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, {
				data: {
					token: "dummytoken",
					email: "XXXXfmff@ssssXXX.com",
					mobileNumber: " XXXXXXX8788",
					passCode: "NEW"
				}
			} );
		}
		return httpPost( cfg.ssoAPI().endPoint() + "users/login/eid", req );
	},
	resendOTP: () => {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: "sdasdasdasds" } );
		}
		return httpGet( cfg.ssoAPI().endPoint() + "users/login/eid/resendotp" );
	},
	verifyOTP: ( otp, method = "eid" ) => {
		const req = {
			otp: otp
		};
		if ( MockConfig.mock ) {
			MockConfig.signedIn = true;
			return later( MockConfig.loadingTime, { data: { token: "dummytoken" } } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + `users/login/${ method }/verifyotp`, req );
	},
	verifyPassCode: ( passCode, method = "eid" ) => {
		const req = {
			key: passCode
		};
		if ( MockConfig.mock ) {
			MockConfig.signedIn = true;
			return later( MockConfig.loadingTime, { data: { token: "dummytoken" } } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + `users/login/${ method }/verifypass`, req );
	},
	generatePassCode: ( method = "eid" ) => {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, {
				data: {
					token: "dummytoken",
					email: "XXXXfmff@ssssXXX.com",
					mobileNumber: " XXXXXXX8788",
					passCode: "NEW"
				}
			} );
		}
		return httpGet( cfg.ssoAPI().endPoint() + `users/login/${ method }/resendpass` );
	},
	user: ( user, password, captchaResponse: string ) => {
		const req = {
			userName: user,
			password: password,
			reCaptchaKey: captchaResponse
		};
		if ( MockConfig.mock ) {
			MockConfig.signedIn = true;
			return later( MockConfig.loadingTime, { data: { token: "dummytoken" } } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + `users/login/uid`, req );
	}
};

export const register: IRegisterApi = {
	eid( eid: string, captchaResponse: string ): Promise<SSOResponse<EIDAServerProfile>> {
		if ( eid ) eid.replace( /^[0-9]*/g, "" );
		const req = {
			emiratesId: eid,
			reCaptchaKey: captchaResponse
		};
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: dummyuEIDAProfile } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + "users/register/eida", req );
	},
	confirm( profile: EIDAServerProfile ): Promise<SSOResponse<EIDAServerProfile>> {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: profile } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + "users/register/eida/confirm", { email: profile.email.key } );
	},
	verify( mobileOTP: string, emailOTP: string ): Promise<SSOResponse<EIDAServerProfile>> {
		const req = {
			otp: {
				email: emailOTP,
				mobile: mobileOTP,
			},
		};
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: dummyEIDAProfile } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + "users/register/eida/verifyotp", req );
	},
	resendOTP( config: { email: boolean; mobile: boolean } ): Promise<any> {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: "sdasdasdasds" } );
		}
		return httpPost( cfg.ssoAPI().endPoint() + "users/register/eida/resendotp", config );
	},
	create( regType: RegType, userName: string, password: string ): Promise<SSOResponse<EIDAServerProfile>> {
		const req = {
			userName,
			password
		};
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: dummyEIDAProfile } );
		}
		let ep = "users/register/eida/upass";
		if ( regType === "uaepass" ) {
			ep = "uaepass/register";
		}
		return httpPost( cfg.ssoAPI().endPoint() + ep, req );
	},
	loadUaePassProfile(): Promise<SSOResponse<EIDAServerProfile>> {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: dummyEIDAProfile } );
		}
		return httpGet( cfg.ssoAPI().endPoint() + "uaepass/register/profile" );
	},
	validateApplication( appId: string ): Promise<SSOResponse<any>> {
		if ( MockConfig.mock ) {
			return later( MockConfig.loadingTime, { data: dummyEIDAProfile } );
		}
		return httpGet( cfg.ssoAPI().endPoint() + "users/checkReg?applicationId=" + appId );
	}
};

export const profile: IProfileApi = {
	me: () => {
		if ( MockConfig.mock ) {
			if ( MockConfig.signedIn ) {
				return later( MockConfig.loadingTime, { data: dummyProfile } );
			} else {
				return Promise.reject( "error" );
			}
		}
		return httpGet( cfg.ssoAPI().endPoint() + `users/me` );
	},
	logout: () => {
		if ( MockConfig.mock ) {
			MockConfig.signedIn = false;
			return later( MockConfig.loadingTime, { data: "" } );
		}
		return httpGet( cfg.ssoAPI().endPoint() + `users/eid/logout` );
	},
	changePassword: ( old, newPwd ) => {
		const req = {
			old,
			new: newPwd
		};
		return httpPut( cfg.ssoAPI().endPoint() + "users/uid/changepass", req );
	},
	copy: ( appId: string, userName: string, password: string ): Promise<any> => {
		const req = {
			userName,
			password
		};
		return httpPost( cfg.ssoAPI().endPoint() + "users/copynewname?applicationId=" + appId, req );
	},
	createUpdateToken(): Promise<SSOResponse<ReqStatus>> {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/access-req", {} );
	},

	verifyUpdateToken( request: ProfileUpdateRequest ): Promise<SSOResponse<ReqStatus>> {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/verify-access-req", { otp: request } );
	},
	initUpdate: ( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>> => {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/new-req", request );
	},

	resendOTPForUpdate: ( request: { email: boolean, mobile: boolean } ): Promise<SSOResponse<ProfileUpdateResponse>> => {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/resendotp", request );
	},

	verifyOTPForUpdate: ( request: { email?: string, mobile?: string } ): Promise<SSOResponse<ProfileUpdateResponse>> => {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/verifyotp", { otp: request } );
	},
	handleProfileException( error: SSOError ): Promise<SSOCorrectiveAction> {
		// let code = error.content?.error?.code;
		// if ( !code ) {
		// 	if ( error.error && error.error.startsWith( "Webservice Error" ) ) {
		// 		code = error.error.replace( "Webservice Error", "" ).trim();
		// 	}
		// }

		let code = error.code;
		if ( code ) {
			const correctiveTask = cfg.ssoAPI().getCorrectiveTask( code );
			if ( correctiveTask ) {
				return Promise.resolve( {
					error: error,
					data: error.content,
					action: {
						id: code,
						task: correctiveTask
					}
				} );
			}
		}
		return Promise.reject( error );
	},

	getProfilePicUri(): string {
		return cfg.ssoAPI().endPoint() + "users/me/pic";
	},
	updateAvatar( photoBase64: string ) {
		const file = DataURIToBlob( photoBase64 );
		const formData = new FormData();
		formData.append( "files", file, "image.jpg" );
		return httpPostRaw( this.getProfilePicUri(), formData );
	},
	initAccountVerify( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>> {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/verify-req", request );
	},
	verifyAccountOtp( request: ProfileUpdateRequest ): Promise<SSOResponse<ProfileUpdateResponse>> {
		return httpPost( cfg.ssoAPI().endPoint() + "users/updateProfile/verify", { otp: request } );
	},

	selectProfile(userAccountId: string): Promise<SSOResponse<any>> {
		return httpPut( cfg.ssoAPI().endPoint() + "users/select?userAccountId=" + userAccountId );
	},

	fetchProfiles(appName){
		return httpGet(cfg.verticalAppAPI().endPoint() + appName);
	},

	fetchNDAs( applicationId: string ): Promise<ProfileResponse<any>> {
		return httpGet(cfg.ndaAPI().endPoint() + "nda/init?applicationId=" + applicationId);
	},

	submitNDA(data: any): Promise<ProfileResponse<any>> {
		return httpPost(cfg.ndaAPI().endPoint() + "nda" , data);
	}
};

export function DataURIToBlob( data: string ) {
	const splitDataURI = data.split( "," );
	const byteString = splitDataURI[ 0 ].indexOf( "base64" ) >= 0 ? atob( splitDataURI[ 1 ] ) : decodeURI( splitDataURI[ 1 ] );
	const mimeString = splitDataURI[ 0 ].split( ":" )[ 1 ].split( ";" )[ 0 ];
	const ia = new Uint8Array( byteString.length );
	for ( let i = 0; i < byteString.length; i++ )
		ia[ i ] = byteString.charCodeAt( i );
	return new Blob( [ ia ], { type: mimeString } );
}

export const forgot: IForgotApi = {
	init: ( userName: string, captchaResponse: string, mobile = "", email = "" ) => {
		const req = {
			userName,
			mobile,
			email,
			reCaptchaKey: captchaResponse,
		};
		return httpPost( cfg.ssoAPI().endPoint() + "users/uid/forgotpass", req );
	},
	resendOTP: () => {
		return httpGet( cfg.ssoAPI().endPoint() + "users/uid/forgotpass/resendotp" );
	},
	verify: otp => {
		const req = {
			otp
		};
		return httpPost( cfg.ssoAPI().endPoint() + "users/uid/forgotpass/verifyotp", req );
	},
	update: newPassword => {
		const req = {
			new: newPassword
		};
		return httpPost( cfg.ssoAPI().endPoint() + "users/uid/forgotpass/update", req );
	}
};

export function isSSOEndpoint( url: string ): boolean {
	return url.indexOf( cfg.ssoAPI().endPoint() ) === 0 || url.indexOf( cfg.verticalAppAPI().endPoint() ) === 0 ||  url.indexOf( cfg.ndaAPI().endPoint() ) === 0;
}


export function silentTokenRefresh( onProfile?: ( profile: Profile ) => void, onError?: ( error: any ) => void ) {
	profile.me().then( profile => {
		if ( onProfile ) onProfile( profile.data );
	} ).catch( e => {
		return exToMessage( e )
		.then( SSOApi.profile.handleProfileException )
		.then( correction => {
			const isCorrected = correction.action.task( correction.data );
			if ( isCorrected ) {
				if ( onProfile ) onProfile( correction.data.data );
				return Promise.resolve();
			}
			return Promise.reject( correction.error );
		} )
		.catch( () => {
			if ( onError ) onError( e );
		} );

	} );
}


export function getUaePassUrl( language: string, state: string ) {
	return cfg.ssoAPI().endPoint() + `uaepass/?state=${ state }&ln=${ language }`;
}

export function getLogoutUrl( language: string, state: string ) {
	return cfg.ssoAPI().endPoint() + `uaepass/?state=${ state }&ln=${ language }&error=`;
}

const dummyProfile: Profile = {
	emiratesId: "784198657419044",
	email: "foo@foo.com",
	fullName: "Foonbar",
	fullNameAr: "Foobar ar",
	mobileNumber: "0582479398",
	lastPassChange: "2035-03-29T22:32:02Z",
	emailVerifiedOn: "2024-02-16T11:28:47Z",
	"notifications": [
		{
			"code": "EmailNotVerified",
			"title": {
				"en": "Email verification expired",
				"ar": "Email verification expired ar"
			},
			"subTitle": {
				"en": "Email verification expired",
				"ar": "Email verification expired ar"
			},
			"type": "CRITICAL"
		}
	],
	applications: [ { applicationId: "7", sso: "ejari", userAccountId: "784198657419044" },
		{ applicationId: "49", sso: "ejari", userAccountId: "784198657419044" } ]
};
const dummyEIDAProfile: EIDAServerProfile = {
	emiratesId: { key: "784198657419044" },
	email: { key: "foo@foo.com" },
	fullNameEn: "Foonbar",
	fullNameAr: "Foobar ar",
	mobileNumber: { key: "0582479398" },
};
const dummyuEIDAProfile: EIDAServerProfile = {
	emiratesId: { key: "784198657419044" },
	email: {}, // for no email
	//  email: {key: "XXfoo@XXfoo.com"},
	fullNameEn: "XXnbar",
	fullNameAr: "XXbar ar",
	mobileNumber: { key: "XX479398" },
};


export function uaePass(): Promise<SSOResponse<EIDAServerProfile>> {
	const req = {};
	if ( MockConfig.mock ) {
		return later( MockConfig.loadingTime, { data: dummyEIDAProfile } );
	}
	return httpPost( cfg.ssoAPI().endPoint() + "users/register/eida/verifyotp", req );
}


type SSOError = { error: string, src?: string, content?: SSOResponse<any>, code?: string };
export type SSOCorrection = { id: string, task: CorrectionTak }
type SSOCorrectiveAction = { error: SSOError, action: SSOCorrection, data?: any };

export function exToMessage( e: any ): Promise<SSOError> {
	if ( e.type === "webservice_error" ) {
		if ( e.source && e.source.json ) {
			return e.source.json().then( ( msg: any ) => {
				let r: SSOResponse<any> = msg;
				let error: string | undefined = undefined;
				let apiError = r && r.error;
				let source: string | undefined = undefined;
				if ( apiError ) {
					source = apiError.src;
					error = LocaleUtil.getLocalValue( apiError );
					if ( StringUtil.isEmptyStringOrNull( error ) ) {
						error = apiError.desc;
					}
					if ( StringUtil.isEmptyStringOrNull( error ) ) {
						error = apiError.code;
					}
				}
				if ( StringUtil.isEmptyStringOrNull( error ) && msg && msg.message ) {
					error = msg.message;
				}
				if ( StringUtil.isEmptyStringOrNull( error ) ) {
					error = e.toString();
				}
				return Promise.resolve( { error: error, content: msg, src: source, code: e.code.toString()} );
			} ).catch( () => {
				return Promise.resolve( { error: e.toString() } );
			} );
		}
	}
	return Promise.resolve( { error: e.toString() } );
}
